import { React, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Typography,
  Box,
  TableBody,
  TableCell,
  TableRow,
  InputBase,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Link,
  Button,
} from "@mui/material";
import { EnhancedTable, stableSort, getSorting } from "../common/EnhancedTable";
import { getTimeNoUTCNoTime } from "../common/dateUtils";

const rowsPerPageOptions = [10, 30, 60, 120];

export default function LicenseList({ authData }) {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showError, setShowError] = useState(false);
  const [simpleErrorMsg, setSimpleErrorMsg] = useState(null);
  const [orgId, setOrgId] = useState("");

  const [pagination, setPagination] = useState({
    order: 'asc',
    orderBy: 'createdAt',
    page: 0,
    rowsPerPage: rowsPerPageOptions[0],
  });

  const savePaginationState = (obj) => {
    setPagination({
      ...pagination,
      order: obj.order,
      orderBy: obj.orderBy,
      page: obj.page,
      rowsPerPage: obj.rowsPerPage,
    });
  };

  useEffect(() => {
    if (authData && authData.authorizedGroups && authData.authorizedGroups.length > 0) {
      const group = authData.authorizedGroups[0];
      const extractedOrgId = group.split('_')[0];
      setOrgId(extractedOrgId);
    }
  }, [authData]);

  useEffect(() => {
    if (orgId) {
      getLicenses();
    }
    // eslint-disable-next-line
  }, [pagination.page, pagination.rowsPerPage, orgId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = licenses.filter(
        (license) =>
          (license.id && license.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (license.orgName && license.orgName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (license.status && license.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (license.acquisitionMethod && license.acquisitionMethod.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (license.autoRenew !== undefined && license.autoRenew !== null && String(license.autoRenew).toLowerCase().includes(searchTerm.toLowerCase())) ||
          (license.startsAt && getTimeNoUTCNoTime(license.startsAt).includes(searchTerm)) ||
          (license.endsAt && getTimeNoUTCNoTime(license.endsAt).includes(searchTerm))
      );
      setFilteredLicenses(filtered);
    } else {
      setFilteredLicenses(licenses); // Reset to full list if search term is empty
    }
  }, [searchTerm, licenses]);

  const getLicenses = () => {
    const url = '/api/licenses';
    const headers = {
      "Content-Type": "application/json", 
    };

    const params = {
      orgId: orgId,
      page: pagination.page,
      size: pagination.rowsPerPage,
    };

    setLoading(true);
    axios({
      url: url,
      method: "GET",
      headers: headers,
      params: params,
    })
      .then((response) => {
        setLicenses(response.data.content);
        setFilteredLicenses(response.data.content);
        setTotalRecords(response.data.page.totalElements);
        setLoading(false);
      })
      .catch((error) => {
        let errorMsg = error.message ? error.message : "Something went wrong";

        if (error.response && error.response.status === 401) {
        errorMsg += ". If this was working previously, please refresh the page.";
      }

        setSimpleErrorMsg(errorMsg);
        setErrorMessage(error.response?.data?.message || "Something went wrong");
        setShowError(true);
        setLoading(false);
      });
    };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const goToLicense = (event, id) => {
    event.preventDefault();
    navigate(`/license/${id}`);
  };

  const renderLicenseList = () => {
    const { order, orderBy, page, rowsPerPage } = pagination;
    const headCells = [
      { id: 'id', label: 'License ID' },
      { id: 'orgName', label: 'Organization Name' },
      { id: 'status', label: 'Status' },
      { id: 'acquisitionMethod', label: 'Acquisition Method' },
      { id: 'autoRenew', label: 'Auto Renew' },
      { id: 'startsAt', label: 'Start Date - End Date' },
    ];

    if ((licenses === null || licenses.length === 0) && !loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Typography variant="h5" color="error">
            {errorMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <div id='licensesList'>
        <EnhancedTable
          headCells={headCells}
          order={order}
          orderBy={orderBy}
          count={totalRecords}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          savePaginationState={savePaginationState}>
          <TableBody>
            {stableSort(filteredLicenses, getSorting(order, orderBy)).map((item, index) => (
              <TableRow key={item.id}>
                <TableCell sx={{ fontSize: '1.1rem' }}>
                  <Link
                    color="primary"
                    style={{ cursor: "pointer" }}
                    onClick={(event) => goToLicense(event, item.id)}
                    underline="hover"
                  >
                    {item.id}
                  </Link>
                </TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.orgName ? item.orgName : 'N/A'}</TableCell>
                <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem' }}>
                  {item.status === 'Expired' ? (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: '#f44336',
                        color: '#fff',
                        borderRadius: '8px',
                        px: 2,
                        py: 0.5,
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                        display: 'inline-block',
                        ml: -7,
                      }}
                    >
                      Expired
                    </Box>
                  ) : item.status === 'ACTIVE' ? (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: '#4caf50',
                        color: '#fff',
                        borderRadius: '8px',
                        px: 2,
                        py: 0.5,
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                        display: 'inline-block',
                        ml: -7,
                      }}
                    >
                      ACTIVE
                    </Box>
                  ) : (
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: '#f44336',  // Red
                          color: '#fff',
                          borderRadius: '8px',
                          px: 2,
                          py: 0.5,
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          display: 'inline-block',
                          ml: -7,
                        }}
                      >
                    {item.status || 'UNKNOWN'}
                  </Box>
                )}
                </TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.acquisitionMethod}</TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.autoRenew !== undefined && item.autoRenew !== null ? String(item.autoRenew) : 'N/A'}</TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.startsAt && item.endsAt ? `${getTimeNoUTCNoTime(item.startsAt)} - ${getTimeNoUTCNoTime(item.endsAt)}` : 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </EnhancedTable>
      </div>
    );
  }

  return (
    <Box sx={{ bgcolor: "primary.contrastText", minHeight: "100vh", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      {loading && (
        <Backdrop
          open={loading}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "common.white",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {showError && (
        <>
          <Snackbar
            open={showError}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 2,
            }}
          >
            <Alert
              onClose={handleCloseError}
              severity="error"
              sx={{
                width: "100%",
                fontSize: "16px",
              }}
            >
              {simpleErrorMsg}
            </Alert>
          </Snackbar>
        </>
      )}
      <Box sx={{ width: "100%", mt: 5 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 5, pr: 4 }}>
          <Box
            component="form"
            onSubmit={e => { e.preventDefault(); }}
            sx={{
              display: "flex",
              alignItems: "center",
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            <Box
              sx={{
                border: "1px solid #bdbdbd",
                borderRadius: 2,
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                pr: 0,
              }}
            >
              <InputBase
                sx={{ width: 260, fontSize: 18, pl: 2, py: 0.5 }}
                placeholder="Search"
                inputProps={{ "aria-label": "search" }}
                value={searchTerm || ""}
                onChange={handleSearchChange}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                ml: 3,
                minWidth: 100,
                minHeight: 36,
                backgroundColor: "#2d186c",
                color: "#fff",
                borderRadius: 999,
                boxShadow: "none",
                fontWeight: 'bold',
                fontSize: '1rem',
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: "#432c8c" },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>
        {renderLicenseList()}
      </Box>
    </Box>
  );
} 